(() => {
  if (window.DinartStockFirst) return;
  window.DinartStockFirst = true;
  const pending = new WeakSet();
  const cache = new Map();
  let loadingMore = false;
  let scrollObserver;
  const cards = grid => Array.from(grid.querySelectorAll(':scope > li.grid__item'));
  const selectedAvailability = url => {
    const values = [...new Set(url.searchParams.getAll('filter.v.availability'))];
    return values.length === 1 && ['0', '1'].includes(values[0]);
  };
  function getGrid(doc) {
    const grid = doc.querySelector('[data-stock-first]');
    if (!grid) throw new Error('Missing collection grid');
    return grid;
  }
  async function request(url) {
    const key = url.toString();
    const existing = cache.get(key);
    if (existing && Date.now() - existing.time < 10000) return existing.promise;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    const promise = fetch(url, { signal: controller.signal, credentials: 'same-origin' })
      .then(response => {
        if (!response.ok) throw new Error('Collection request failed');
        return response.text();
      })
      .then(html => new DOMParser().parseFromString(html, 'text/html'))
      .finally(() => clearTimeout(timeout));
    cache.set(key, { time: Date.now(), promise });
    promise.catch(() => cache.delete(key));
    return promise;
  }
  async function orderedCards(grid, pageUrl) {
    const original = cards(grid);
    if (selectedAvailability(pageUrl)) return original;
    const size = Number(grid.dataset.stockSize);
    const total = Number(grid.dataset.stockTotal);
    const page = Number(grid.dataset.stockPage) || 1;
    if (!size || !total) return original;
    const groupPage = async (available, number) => {
      const url = new URL(pageUrl);
      url.searchParams.delete('filter.v.availability');
      url.searchParams.set('filter.v.availability', available);
      url.searchParams.set('page', number);
      url.searchParams.set('section_id', grid.dataset.id);
      return getGrid(await request(url));
    };
    const first = await groupPage('1', 1);
    if (cards(first).some(item => item.dataset.stockAvailable !== 'true')) {
      throw new Error('Availability filter was not applied');
    }
    const inStock = Number(first.dataset.stockTotal);
    if (!Number.isFinite(inStock) || inStock < 0 || inStock > total) {
      throw new Error('Invalid collection count');
    }
    if (inStock === 0 || inStock === total) return original;
    const start = (page - 1) * size;
    const expected = Math.max(0, Math.min(size, total - start));
    const result = [];
    if (start < inStock) {
      const source = page === 1 ? first : await groupPage('1', page);
      const availableCards = cards(source);
      if (availableCards.some(item => item.dataset.stockAvailable !== 'true')) {
        throw new Error('Invalid in-stock results');
      }
      result.push(...availableCards.slice(0, Math.min(size, inStock - start)));
    }
    if (result.length < expected) {
      const offset = Math.max(0, start - inStock);
      const firstPage = Math.floor(offset / size) + 1;
      const skip = offset % size;
      const needed = expected - result.length;
      const sources = await Promise.all([
        groupPage('0', firstPage),
        skip + needed > size ? groupPage('0', firstPage + 1) : Promise.resolve(null)
      ]);
      const unavailableCards = sources.flatMap(source => source ? cards(source) : []);
      if (unavailableCards.some(item => item.dataset.stockAvailable !== 'false')) {
        throw new Error('Invalid sold-out results');
      }
      result.push(...unavailableCards.slice(skip, skip + needed));
    }
    if (result.length !== expected || new Set(result.map(item => item.dataset.stockId)).size !== expected) {
      throw new Error('Incomplete collection page');
    }
    return result.map(item => item.cloneNode(true));
  }
  function prepareForms() {
    document.querySelectorAll('form[role="search"]').forEach(form => {
      let input = form.querySelector('input[name="options[unavailable_products]"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'options[unavailable_products]';
        form.append(input);
      }
      input.value = 'last';
    });
  }
  async function prepare(grid) {
    if (grid.dataset.stockReady || pending.has(grid)) return;
    pending.add(grid);
    grid.setAttribute('aria-busy', 'true');
    grid.classList.add('dinart-stock-preparing');
    try {
      const ordered = await orderedCards(grid, new URL(location.href));
      if (!grid.isConnected) return;
      grid.replaceChildren(...ordered);
    } catch (error) {
      console.warn('Dinart stock ordering:', error.message);
    } finally {
      grid.dataset.stockReady = 'true';
      grid.removeAttribute('aria-busy');
      grid.classList.remove('dinart-stock-preparing');
      pending.delete(grid);
      observeScroll();
    }
  }
  async function loadMore(link, pagination) {
    if (loadingMore) return;
    const current = document.querySelector('[data-stock-first]');
    if (!current || !current.dataset.stockReady) return;
    loadingMore = true;
    link.setAttribute('aria-busy', 'true');
    pagination.setAttribute('aria-busy', 'true');
    const url = new URL(link.href);
    try {
      url.searchParams.set('section_id', current.dataset.id);
      const doc = await request(url);
      const next = getGrid(doc);
      const ordered = await orderedCards(next, url);
      if (current !== document.querySelector('[data-stock-first]') || !current.isConnected) return;
      const existing = new Set(cards(current).map(item => item.dataset.stockId));
      if (ordered.some(item => existing.has(item.dataset.stockId))) throw new Error('Duplicate collection page');
      current.append(...ordered);
      const nextPagination = doc.querySelector('.AjaxinatePagination');
      if (nextPagination) pagination.replaceChildren(...nextPagination.childNodes);
      else pagination.replaceChildren();
      if (window.AOS && typeof window.AOS.refresh === 'function') window.AOS.refresh();
    } catch (error) {
      const fallback = new URL(link.href);
      fallback.searchParams.delete('section_id');
      location.assign(fallback.toString());
    } finally {
      loadingMore = false;
      link.removeAttribute('aria-busy');
      pagination.removeAttribute('aria-busy');
      observeScroll();
    }
  }
  function observeScroll() {
    if (scrollObserver) scrollObserver.disconnect();
    const pagination = document.querySelector('.AjaxinatePagination[data-pagination="scroll"]');
    if (!pagination || !pagination.querySelector('a') || !('IntersectionObserver' in window)) return;
    scrollObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        const link = pagination.querySelector('a');
        if (link) loadMore(link, pagination);
      }
    }, { rootMargin: '160px' });
    scrollObserver.observe(pagination);
  }
  function scan() {
    prepareForms();
    document.querySelectorAll('[data-stock-first]').forEach(prepare);
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('.AjaxinatePagination a');
    if (!link || !document.querySelector('[data-stock-first]') || event.defaultPrevented || event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const grid = document.querySelector('[data-stock-first]');
    if (!grid.dataset.stockReady) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    loadMore(link, link.closest('.AjaxinatePagination'));
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.matches('form[role="search"]')) prepareForms();
  }, true);
  const start = () => {
    scan();
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        queueMicrotask(() => { queued = false; scan(); });
      }).observe(container, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
